import React from 'react';

import BookNewCoachingSessionSidebar from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CoachingConstants from 'coaching/CoachingConstants';
import ManageRecurringSessionSidebar from 'coaching/components/CoachingHomeView/ManageRecurringSessionSidebar';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import MediaObject from 'components/MediaObject';
import { getCoachTitle } from 'coaching/lib/nameFormat';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';
import { useCoachingViewState } from 'coaching/components/CoachingView';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';
import { TimeslotFrequencyInput } from 'globalTypes';
import {
  CoachMetadata,
  CoachingSessionSchedule,
  CoachingSessionInstance,
  CoachingSessionTools,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';

import 'coaching/components/styles/CoachingSessionsBySubjectContainer.scss';

const userIsOnDesktop = userAgent.isDesktop();

type Props = {
  subjectName?: string;
  recurringSessionSchedules?: CoachingSessionSchedule[];
  upcomingInstances?: CoachingSessionInstance[];
  children: React.ReactNode;
  sessionTools?: CoachingSessionTools;
  previousCoaches: CoachMetadata[];
  participantUuid: string;
};

export default function CoachingSessionsBySubjectBox(props: Props) {
  return (
    <Row>
      <Box noShadow textAlign="left" bandHeader={<SubjectHeader {...props} />}>
        <BoxBody full flush divided={true}>
          {props.children}
        </BoxBody>
      </Box>
    </Row>
  );
}

function SubjectHeader({
  subjectName,
  recurringSessionSchedules,
  upcomingInstances,
  sessionTools,
  previousCoaches,
  participantUuid,
}: Props) {
  if (!subjectName) return null;

  const defaultCoach = recurringSessionSchedules?.[0]?.coach ?? previousCoaches?.[0];
  return (
    <MediaObject figure="right" gutter="small">
      <SubjectTextRow
        recurringSessionSchedules={recurringSessionSchedules}
        subjectName={subjectName}
      />
      <Buttons reset="none">
        {upcomingInstances && upcomingInstances.length > 0 && defaultCoach && sessionTools && (
          <AddSessionButton
            subjectName={subjectName}
            defaultCoach={defaultCoach}
            sessionTools={sessionTools}
            hasExistingRecurringSchedule={Boolean(
              recurringSessionSchedules && recurringSessionSchedules.length > 0
            )}
            participantUuid={participantUuid}
          />
        )}
        {recurringSessionSchedules && (
          <ManageSessionButton
            subjectName={subjectName}
            defaultCoach={defaultCoach}
            sessionTools={sessionTools}
            recurringSessionSchedules={recurringSessionSchedules}
            participantUuid={participantUuid}
            upcomingInstances={upcomingInstances}
          />
        )}
      </Buttons>
    </MediaObject>
  );
}

function SubjectTextRow({
  recurringSessionSchedules,
  subjectName,
}: {
  recurringSessionSchedules?: CoachingSessionSchedule[];
  subjectName: string;
}) {
  const cadenceInformation = calculateCadenceInformation(recurringSessionSchedules);
  const coachNameString = Boolean(recurringSessionSchedules?.length)
    ? `with ${recurringSessionSchedules?.map(s => getCoachTitle(s.coach.name)).join(', ')}`
    : undefined;
  const textComponent = (
    <Text weight="semibold" size="medium">
      {userIsOnDesktop ? `${subjectName} ${coachNameString ?? ''}` : subjectName}
    </Text>
  );
  return userIsOnDesktop ? (
    <MediaObject center gutter="small">
      {textComponent}
      {cadenceInformation}
    </MediaObject>
  ) : (
    <>
      <Row size="xxsmall">{textComponent}</Row>
      {cadenceInformation}
    </>
  );
}

function AddSessionButton({
  subjectName,
  defaultCoach,
  participantUuid,
  hasExistingRecurringSchedule,
  sessionTools,
}: {
  subjectName: string;
  defaultCoach: CoachMetadata;
  participantUuid: string;
  hasExistingRecurringSchedule: boolean;
  sessionTools: CoachingSessionTools;
}) {
  const { setSidebarIsOpen, setSidebar } = useCoachingViewState();
  return (
    <Button
      eventKey={`${coachingHomeEventPrefix}.add_session.click`}
      color="mascot"
      data-test="subject-header-add-session-button"
      level="icon"
      onClick={() => {
        setSidebar(
          <BookNewCoachingSessionSidebar
            subjectName={subjectName}
            coach={defaultCoach}
            participantUuid={participantUuid}
            defaultFrequency={TimeslotFrequencyInput.once}
            defaultStep={hasExistingRecurringSchedule ? 'book-session' : 'frequency-selection'}
            sessionTools={sessionTools}
            onClose={() => setSidebarIsOpen(false)}
            showFrequencySelect={hasExistingRecurringSchedule ? false : true}
          />
        );
        setSidebarIsOpen(true);
      }}>
      <TextWithIcon
        className="CoachingSessionsBySubjectContainer-rescheduleButton"
        icon="+"
        size="small"
        weight="semibold">
        {userIsOnDesktop ? 'Add session' : 'Add'}
      </TextWithIcon>
    </Button>
  );
}

function ManageSessionButton({
  subjectName,
  defaultCoach,
  participantUuid,
  recurringSessionSchedules,
  sessionTools,
  upcomingInstances,
}: {
  subjectName: string;
  defaultCoach: CoachMetadata;
  participantUuid: string;
  recurringSessionSchedules: CoachingSessionSchedule[];
  sessionTools?: CoachingSessionTools;
  upcomingInstances?: CoachingSessionInstance[];
}) {
  const { setSidebarIsOpen, setSidebar } = useCoachingViewState();

  return (
    <Button
      color="mascot"
      level="icon"
      eventKey={`${coachingHomeEventPrefix}.manage_plan.click`}
      onClick={() => {
        setSidebar(
          <ManageRecurringSessionSidebar
            participantUuid={participantUuid}
            recurringCoachingSessionSchedules={recurringSessionSchedules}
            upcomingInstances={upcomingInstances}
            subjectName={subjectName}
            onClose={() => setSidebarIsOpen(false)}
            onClickBookSession={
              sessionTools && defaultCoach
                ? () =>
                    setSidebar(
                      <BookNewCoachingSessionSidebar
                        subjectName={subjectName}
                        sessionTools={sessionTools}
                        coach={defaultCoach}
                        onClose={() => setSidebarIsOpen(false)}
                        participantUuid={participantUuid}
                      />
                    )
                : // We should theoretically never hit this since we'd expect a user who's managing their recurring
                  // sessions to have session tools + a default coach, but including it just to be safe.
                  () =>
                    window.open(
                      `${CoachingConstants.COACHING_HELP_FORM_ROUTE}?request=c_new_session`,
                      '_blank'
                    )
            }
          />
        );
        setSidebarIsOpen(true);
      }}>
      <TextWithIcon icon="calendar" size="small" weight="semibold">
        {userIsOnDesktop ? 'Manage plan' : 'Manage'}
      </TextWithIcon>
    </Button>
  );
}

function hasFreeTrial(instances?: CoachingSessionInstance[]) {
  return instances?.some(i => i.isFreeTrial);
}

function calculateCadenceInformation(
  recurringSchedules?: CoachingSessionSchedule[],
  instances?: CoachingSessionInstance[]
) {
  function CadenceText({ children }: { children: React.ReactNode }) {
    return (
      <Text size="xsmall" color="graphite">
        {children}
      </Text>
    );
  }

  if (hasFreeTrial(instances)) {
    return <CadenceText>Free trial session</CadenceText>;
  } else if (recurringSchedules?.length === 1) {
    return <CadenceText>{recurringSchedules[0].description}</CadenceText>;
  } else {
    return null;
  }
}
